import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { colors, colorSystem } from '@/styles/theme'
import { isNil } from 'lodash-es'
import { Icon, HStack, VStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'

interface Props {
  className?: string
  contentClassName?: string
  type?: 'v' | 'h'
  list: {
    title?: string | React.ReactNode
    useToggle?: boolean
    subTitle?: string | React.ReactNode
    content: React.ReactNode
    initOpen?: boolean
    tableRadius?: string
  }[]
  contentMinHeight?: number
}

function ResizableContents({ type = 'v', ...rest }: Props) {
  return type === 'v' ? <DragVertical {...rest} /> : <DragHorizontal {...rest} />
}

function DragVertical({ className, contentClassName, list = [], contentMinHeight = 400 }: Omit<Props, 'type'>) {
  const [tableHeights, setTableHeights] = useState<number[]>(Array(list.length).fill(contentMinHeight))
  const [contentVisibility, setContentVisibility] = useState<boolean[]>(Array(list.length).fill(true))
  const draggingIndex = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement[]>([])
  const setListRef = useCallback(
    (el: HTMLDivElement | null, idx: number) => {
      if (el) listRef.current[idx] = el
    },
    [listRef],
  )

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const deltaY = event.movementY

    setTableHeights((prevHeights) => {
      const newHeights = [...prevHeights]
      if (isNil(draggingIndex) || isNil(draggingIndex.current)) return newHeights
      newHeights[draggingIndex.current] = prevHeights[draggingIndex.current] + deltaY
      const isLastBarMoved = draggingIndex.current === list.length - 2
      newHeights[draggingIndex.current + 1] = isLastBarMoved
        ? prevHeights[draggingIndex.current + 1]
        : prevHeights[draggingIndex.current + 1] - deltaY

      return newHeights
    })
  }, [])

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    draggingIndex.current = null
  }, [handleMouseMove])

  const handleMouseDown = useCallback(
    (index: number) => () => {
      draggingIndex.current = index
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, handleMouseUp],
  )

  const toggleContentVisibility = useCallback((index: number) => {
    setContentVisibility((prevVisibility) => {
      const newVisibility = [...prevVisibility]
      newVisibility[index] = !newVisibility[index]
      return newVisibility
    })
  }, [])

  useEffect(() => {
    setContentVisibility(list.map((item) => item.initOpen ?? true))
  }, [list])

  useEffect(() => {
    if (!listRef) return
    if (!Array.isArray(listRef.current)) return
    setTableHeights(listRef.current?.map((item) => item.offsetHeight))
  }, [listRef])

  return (
    <Container className={className}>
      {list.map((table, index) => {
        const { title, subTitle, tableRadius, content, useToggle = true } = table

        return (
          <React.Fragment key={`resizable-contents-vertical-${title}-${index}`}>
            {title && (
              <GridTitle
                $isFirst={index === 0}
                $isLast={index === list.length - 1}
                $open={contentVisibility[index]}
                kind="Headline_16_bold"
                onClick={useToggle ? () => toggleContentVisibility(index) : undefined}
              >
                <TitleContainer align="center" gap={8}>
                  {useToggle && <Icon name={contentVisibility[index] ? 'arrow-down-filled' : 'arrow-up-filled'} size={24} color="gray800" />}
                  {title}
                  {subTitle && (
                    <Text kind="Body_14_medium" color={colors.gray800}>
                      {subTitle}
                    </Text>
                  )}
                </TitleContainer>
              </GridTitle>
            )}

            <VContent
              className={contentClassName}
              ref={(el) => setListRef(el, index)}
              style={
                tableHeights[index] && contentVisibility[index] ? { height: `${tableHeights[index]}px`, overflowY: 'inherit' } : { display: 'none' }
              }
              $radius={tableRadius}
            >
              {content}
            </VContent>
            {index < list.length - 1 && (
              <DragWrap align="center" justify="center" p={4} onMouseDown={handleMouseDown(index)}>
                <Icon name="drag-h" size={16} />
              </DragWrap>
            )}
          </React.Fragment>
        )
      })}
    </Container>
  )
}

const MIN_DRAG_GAP = 10
function DragHorizontal({ className, contentClassName, list = [] }: Omit<Props, 'type'>) {
  const [tableWidths, setTableWidths] = useState<number[]>(Array(list.length).fill(400))
  const draggingIndex = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement[]>([])

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const deltaX = event.movementX
    setTableWidths((prevWidths) => {
      const newWidths = [...prevWidths]
      if (isNil(draggingIndex) || isNil(draggingIndex.current)) return newWidths
      const newWidthLeft = prevWidths[draggingIndex.current] + deltaX
      const newWidthRight = prevWidths[draggingIndex.current + 1] - deltaX
      if (newWidthLeft > MIN_DRAG_GAP && newWidthRight > MIN_DRAG_GAP) {
        newWidths[draggingIndex.current] = newWidthLeft
        newWidths[draggingIndex.current + 1] = newWidthRight
      }
      return newWidths
    })
  }, [])

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    draggingIndex.current = null
  }, [handleMouseMove])

  const handleMouseDown = useCallback(
    (index: number) => () => {
      draggingIndex.current = index
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, handleMouseUp],
  )

  useEffect(() => {
    if (!Array.isArray(listRef.current)) return
    setTableWidths(listRef.current.map((item) => item.offsetWidth))
  }, [listRef])

  return (
    <HStack className={className} style={{ width: '100%' }}>
      {list.map((item, index) => (
        <React.Fragment key={`resizable-contents-horizontal-${index}`}>
          <HContent
            $title={!!item.title}
            className={contentClassName}
            ref={(el) => {
              if (el) listRef.current[index] = el
            }}
            style={tableWidths[index] ? { width: `${tableWidths[index]}px` } : {}}
          >
            {item.title && (
              <GridTitle isHorizontal kind="Headline_16_bold">
                {item.title}
              </GridTitle>
            )}
            <HContentScroll>{item.content}</HContentScroll>
          </HContent>
          {index < list.length - 1 && (
            <DragWrap $isHorizontal align="center" justify="center" p={4} onMouseDown={handleMouseDown(index)}>
              <Icon name="drag-v" size={16} />
            </DragWrap>
          )}
        </React.Fragment>
      ))}
    </HStack>
  )
}

const Container = styled(VStack)`
  padding-bottom: 24px;
`
const DragWrap = styled(HStack)<{ $isHorizontal?: boolean }>`
  background-color: ${colors.gray100};
  cursor: ${({ $isHorizontal }) => ($isHorizontal ? 'col-resize' : 'row-resize')};

  > i {
    border-radius: 4px;
    background-color: ${colors.yellow500};
    padding: ${({ $isHorizontal }) => ($isHorizontal ? '20px 0' : '0 20px')};
  }
`

const VContent = styled.div<{ $radius?: string }>`
  overflow-y: auto;

  .ag-theme-balham {
    ${({ $radius }) => $radius && `border-radius: ${$radius} !important;`}
    .ag-root-wrapper.ag-layout-normal {
      ${({ $radius }) => $radius && `border-radius: ${$radius} !important;`}
    }
  }
`

const HContent = styled.div<{ $title?: boolean }>`
  flex-grow: 1;
  position: relative;
  padding-top: ${({ $title }) => ($title ? 44 : 0)}px;

  .ag-theme-balham {
    border-radius: 0;
    .ag-root-wrapper.ag-layout-normal {
      border-radius: 0;
    }
  }
`

const GridTitle = styled(Text)<{ $open?: boolean; isHorizontal?: boolean; $isFirst?: boolean; $isLast?: boolean }>`
  width: 100%;
  padding: 12px 16px;

  ${({ isHorizontal, $open, $isLast }) =>
    !isHorizontal &&
    !$open &&
    css`
      ${$isLast &&
      css`
        border-radius: 0 0 8px 8px;
      `}
      border-bottom: 1px solid ${colorSystem.border.table};
    `}

  ${({ isHorizontal, $isFirst }) =>
    !isHorizontal &&
    $isFirst &&
    css`
      border-radius: 8px 8px 0 0;
      border-top: 1px solid ${colorSystem.border.table};
    `}
  

  ${({ isHorizontal }) =>
    isHorizontal
      ? css`
          position: absolute;
          top: 0;
          z-index: 1;
          background-color: ${colors.white};
        `
      : css`
          border-left: 1px solid ${colorSystem.border.table};
          border-right: 1px solid ${colorSystem.border.table};
        `}
`

const TitleContainer = styled(HStack)`
  width: 100%;
`
const HContentScroll = styled.div`
  overflow: auto;
  height: 100%;
`

export default ResizableContents
